import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ReconciliationState, WasteManifestSource } from "generated-graphql/graphql";

import { RECONCILIATION_LABELS } from "../../Shipments/reconciliationDisplay";
import { getDocumentState } from "../documentStateDisplay";
import { SummaryItem } from "./summaryStatus";

/** A bill of lading is never filed in e-Manifest, so only these two sources apply. */
export const BILL_OF_LADING_SOURCES: readonly WasteManifestSource[] = [
  WasteManifestSource.ManifestDocument,
  WasteManifestSource.Vendor,
];

/** How a Data Sources chip renders; a missing source is greyed with an X. */
export type SourceChipState =
  | { status: "present" }
  | { status: "missing" }
  | { status: "needsReview"; onClick?: () => void };

/**
 * The Upload and Vendor chips of a bill of lading. Upload follows the live
 * document state the review line reads, so it matches that line before
 * process-manifests catches up. Vendor is lit when `availableSources` has it.
 */
export function getBillSourceChips(input: {
  sourceDocument: BillSummaryInput["sourceDocument"];
  availableSources: readonly WasteManifestSource[];
  onReviewDocument?: () => void;
}): Partial<Record<WasteManifestSource, SourceChipState>> {
  const state = getDocumentState({ sourceDocument: input.sourceDocument });
  let upload: SourceChipState = { status: "needsReview", onClick: input.onReviewDocument };
  if (state === "approved") upload = { status: "present" };
  if (state === "none") upload = { status: "missing" };
  return {
    [WasteManifestSource.ManifestDocument]: upload,
    [WasteManifestSource.Vendor]: {
      status: input.availableSources.includes(WasteManifestSource.Vendor) ? "present" : "missing",
    },
  };
}

export const E_MANIFEST_NOTE = "Not filed in e-Manifest. There is nothing to reconcile with EPA.";

export interface BillShipmentLink {
  id: string;
  manifestRequestNumber: string;
  reconciliationStatus: ReconciliationState;
}

export interface BillSummaryInput {
  sourceDocument: Parameters<typeof getDocumentState>[0]["sourceDocument"];
  /** Reviewed, but the bill has not counted the review yet. */
  awaitingRecompute: boolean;
  onViewDocument?: () => void;
  /** Null off container tracking; `{ link: null }` once loaded with nothing linked. */
  shipment: { tenantId: string | undefined; link: BillShipmentLink | null } | null;
}

/** The bill has an upload whose data has not been confirmed yet. */
export function billAwaitingReview(sourceDocument: BillSummaryInput["sourceDocument"]): boolean {
  const state = getDocumentState({ sourceDocument });
  return state !== "approved" && state !== "none";
}

/** Only a confirmed bill gets a line: an unconfirmed one gets the manifest's review banner instead. */
function reviewItem(input: BillSummaryInput): SummaryItem[] {
  if (getDocumentState({ sourceDocument: input.sourceDocument }) !== "approved") return [];
  const reviewedAt = input.sourceDocument?.documentMetadata?.reviewedAt;
  return [
    {
      key: "billReview",
      message: `Confirmed ${reviewedAt ? getUtcDisplayDate(reviewedAt) : ""}`.trim(),
      level: "ok",
      tooltip: input.awaitingRecompute
        ? "Its data will appear on this bill of lading within a minute or two."
        : undefined,
      actions: input.onViewDocument
        ? [{ label: "View document", onClick: input.onViewDocument }]
        : [],
    },
  ];
}

function shipmentItem(shipment: BillSummaryInput["shipment"]): SummaryItem[] {
  if (!shipment) return [];
  const { link, tenantId } = shipment;
  if (!link)
    return [{ key: "billShipment", message: "Not linked to a shipment", level: "neutral" }];
  return [
    {
      key: "billShipment",
      message: "Linked to shipment",
      level: "ok",
      link: {
        text: link.manifestRequestNumber,
        to: tenantId ? `/o/${tenantId}/waste/shipments/${link.id}` : undefined,
      },
      trailingText: RECONCILIATION_LABELS[link.reconciliationStatus],
    },
  ];
}

/**
 * The Summary lines of a bill of lading, in order: review status, shipment
 * link, then the e-Manifest note. Each of the first two is left out when it
 * does not apply (no source document, tenant off container tracking).
 */
export function buildBillOfLadingSummaryItems(input: BillSummaryInput): SummaryItem[] {
  return [
    ...reviewItem(input),
    ...shipmentItem(input.shipment),
    { key: "billEManifestNote", message: E_MANIFEST_NOTE, level: "info" },
  ];
}
